import type { PropsWithChildren } from "react";
import { useCallback, useRef } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import biApi from "../../../../../api/biApi";
import type { ParameterDictionary, ReportSource } from "../../../../../api/types/workbookRuns.types";
import { TextFiltersDictionaryItemsContext } from "./TextFiltersDictionaryItemsContext";

type FilterIdentifier = string;
type ClientCode = string;
type DictionaryResult = ParameterDictionary[];

const getDictionaryItemsRequest = withErrorHandling((reportSource: ReportSource, parameterName: string) =>
  biApi.getParameterDictionary(reportSource, parameterName ?? "")
);

const getProcessingItemKey = (filterIdentifier: string, clientCode: string) => {
  return `${filterIdentifier}-${clientCode}`;
};

export const TextFiltersDictionaryItemsContextProvider = ({ children }: PropsWithChildren) => {
  const { sendNotificationError } = useNotificationContext();

  const pendingRequestsRef = useRef<Map<string, Promise<DictionaryResult | undefined>>>(new Map());
  const cacheRef = useRef<Record<FilterIdentifier, Record<ClientCode, DictionaryResult>>>({});

  const getDictionaryItems = useCallback(
    (parameterName: string, clientCode: string, reportSource: ReportSource): Promise<DictionaryResult | undefined> => {
      const key = getProcessingItemKey(parameterName, clientCode);

      const cachedItem = cacheRef.current[parameterName]?.[clientCode];
      if (cachedItem) {
        return Promise.resolve(cachedItem);
      }

      const pendingRequest = pendingRequestsRef.current.get(key);
      if (pendingRequest) {
        return pendingRequest;
      }

      const requestPromise = (async (): Promise<DictionaryResult | undefined> => {
        try {
          const [data, error] = await getDictionaryItemsRequest(reportSource, parameterName);
          if (error) {
            sendNotificationError(error.message ?? "Failed to load dictionary items");
            return undefined;
          }

          const result: DictionaryResult =
            data.dictionaryItems.map(
              (item) =>
                ({
                  ...item.items,
                  subValues: item.subValues,
                }) as ParameterDictionary
            ) ?? [];

          cacheRef.current = {
            ...cacheRef.current,
            [parameterName]: {
              ...cacheRef.current[parameterName],
              [clientCode]: result,
            },
          };

          return result;
        } catch {
          sendNotificationError("Failed to load dictionary items");
          return undefined;
        } finally {
          pendingRequestsRef.current.delete(key);
        }
      })();

      pendingRequestsRef.current.set(key, requestPromise);

      return requestPromise;
    },
    [sendNotificationError]
  );

  return (
    <TextFiltersDictionaryItemsContext value={{ getDictionaryItems }}>{children}</TextFiltersDictionaryItemsContext>
  );
};
